import React from 'react'

/**
 * Common Layout Patterns for BabyKingdom Pages
 * Use these as building blocks for consistent internal page designs
 */

interface EmptyStateProps {
  emoji: string
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
  }
}

export function EmptyState({ emoji, title, description, action }: EmptyStateProps): React.ReactElement {
  return (
    <div className="text-center py-16 px-8">
      <div className="text-8xl mb-4">{emoji}</div>
      <h3 className="text-3xl font-black text-purple-700 mb-2">{title}</h3>
      <p className="text-lg text-gray-600 font-semibold mb-6 max-w-md mx-auto">{description}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-8 py-3 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-black rounded-full shadow-lg hover:shadow-xl transition transform hover:scale-105"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

interface ProgressItemProps {
  icon: string
  title: string
  progress: number
  total: number
  badge?: string
  badgeColor?: string
}

export function ProgressItem({ 
  icon, 
  title, 
  progress, 
  total, 
  badge, 
  badgeColor = 'bg-pink-400' 
}: ProgressItemProps): React.ReactElement {
  const percentage = (progress / total) * 100

  return (
    <div className="p-6 bg-gradient-to-r from-purple-100 to-pink-50 rounded-2xl border-3 border-purple-200 shadow-lg">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{icon}</span>
          <h3 className="text-xl font-black text-purple-700">{title}</h3>
        </div>
        {badge && (
          <span className={`${badgeColor} text-white px-3 py-1 rounded-full text-sm font-bold`}>
            {badge}
          </span>
        )}
      </div>
      <div className="w-full bg-purple-200 rounded-full h-4 border-2 border-purple-300">
        <div
          className="bg-gradient-to-r from-pink-400 to-purple-400 h-full rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-sm text-purple-600 font-bold mt-2">
        {progress}/{total} Complete
      </p>
    </div>
  )
}

interface RankItemProps {
  rank: number
  name: string
  value: string
  valueLabel: string
  medal?: boolean
}

export function RankItem({ rank, name, value, valueLabel, medal }: RankItemProps): React.ReactElement {
  const isTopThree = rank <= 3
  const medals = { 1: '👑', 2: '🥈', 3: '🥉' }

  return (
    <div
      className={`p-6 rounded-2xl border-3 flex items-center justify-between transition transform hover:-translate-y-1 ${
        isTopThree
          ? 'bg-gradient-to-r from-yellow-100 to-orange-50 border-yellow-300 shadow-lg'
          : 'bg-white border-purple-200 shadow-md'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="text-4xl font-black text-purple-700 w-12 text-center">
          {isTopThree && medals[rank as keyof typeof medals]}
          {!isTopThree && rank}
        </div>
        <div>
          <p className="text-lg font-black text-purple-700">{name}</p>
          <p className="text-sm text-gray-600">Member</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-2xl font-black text-pink-600">{value}</p>
        <p className="text-sm font-bold text-gray-600">{valueLabel}</p>
      </div>
    </div>
  )
}

interface HeroImageProps {
  emoji: string
  size?: 'sm' | 'md' | 'lg'
}

export function HeroImage({ emoji, size = 'md' }: HeroImageProps): React.ReactElement {
  const sizes = {
    sm: 'text-6xl',
    md: 'text-8xl',
    lg: 'text-9xl'
  }

  return (
    <div className="flex justify-center items-center p-8">
      <div className={`${sizes[size]} drop-shadow-2xl animate-bounce`}>
        {emoji}
      </div>
    </div>
  )
}

interface FilterChipsProps {
  options?: { label: string; value: string }[]
  chips?: { label: string; selected: boolean; onClick: () => void }[]
  selected?: string[]
  onChange?: (selected: string[]) => void
  maxSelectable?: number
}

export function FilterChips({ options, chips, selected = [], onChange, maxSelectable }: FilterChipsProps): React.ReactElement {
  // Support both patterns: options/selected/onChange and chips
  if (chips) {
    return (
      <div className="flex flex-wrap gap-3 mb-6">
        {chips.map((chip, idx) => (
          <button
            key={idx}
            onClick={chip.onClick}
            className={`px-4 py-2 rounded-full font-black text-sm transition ${
              chip.selected
                ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-lg'
                : 'bg-white border-2 border-purple-200 text-purple-700 hover:border-pink-400'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>
    )
  }

  if (!options || !onChange) return <div />

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value))
    } else if (!maxSelectable || selected.length < maxSelectable) {
      onChange([...selected, value])
    }
  }

  return (
    <div className="flex flex-wrap gap-3 mb-6">
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => toggleOption(option.value)}
          className={`px-4 py-2 rounded-full font-black text-sm transition ${
            selected.includes(option.value)
              ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-lg'
              : 'bg-white border-2 border-purple-200 text-purple-700 hover:border-pink-400'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

interface AchievementBadgeProps {
  emoji: string
  title: string
  description: string
  unlocked: boolean
}

export function AchievementBadge({ emoji, title, description, unlocked }: AchievementBadgeProps): React.ReactElement {
  return (
    <div
      className={`p-6 rounded-2xl border-3 text-center transition transform hover:-translate-y-1 ${
        unlocked
          ? 'bg-gradient-to-br from-yellow-100 to-orange-50 border-yellow-300 shadow-lg'
          : 'bg-gray-100 border-gray-300 shadow-md opacity-50'
      }`}
    >
      <div className={`text-5xl mb-3 ${unlocked ? '' : 'grayscale'}`}>{emoji}</div>
      <h4 className={`text-lg font-black mb-1 ${unlocked ? 'text-purple-700' : 'text-gray-500'}`}>
        {title}
      </h4>
      <p className={`text-sm font-semibold ${unlocked ? 'text-gray-700' : 'text-gray-400'}`}>
        {description}
      </p>
    </div>
  )
}

interface AlertProps {
  emoji?: string
  icon?: string
  title: string
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  className?: string
}

export function Alert({ emoji, icon, title, message, type = 'info', className = '' }: AlertProps): React.ReactElement {
  const displayIcon = emoji || icon || '📋'
  const typeClasses = {
    info: 'bg-blue-100 border-blue-300 text-blue-700',
    success: 'bg-green-100 border-green-300 text-green-700',
    warning: 'bg-yellow-100 border-yellow-300 text-yellow-700',
    error: 'bg-red-100 border-red-300 text-red-700'
  }

  return (
    <div className={`p-6 rounded-2xl border-3 ${typeClasses[type]} shadow-lg ${className}`}>
      <div className="flex items-start gap-4">
        <div className="text-4xl">{displayIcon}</div>
        <div>
          <h4 className="font-black text-lg mb-1">{title}</h4>
          <p className="font-semibold">{message}</p>
        </div>
      </div>
    </div>
  )
}
